import omitProps from '@hocs/omit-props';
import withLifecycle from '@hocs/with-lifecycle';
import { bool, string } from 'prop-types';
import { contains, filter, find, isNil, join, not, pathOr, pick, T } from 'ramda';
import { noop } from 'ramda-adjunct';
import { connect } from 'react-refetch';
import { withRouter } from 'react-router';
import {
  compose,
  defaultProps,
  getContext,
  setDisplayName,
  setPropTypes,
  withContext,
  withHandlers,
  withProps,
  withPropsOnChange,
  withState,
  withStateHandlers,
} from 'recompose';
import { Subject } from 'rxjs';
import { DEFAULT_PAGE_SIZE, GOOGLE_PHOTOS_PAGE_SIZE, UNSPLASH_PAGE_SIZE } from '../../../constants/PageSize';
import { SORTING_OPTIONS } from '../../../constants/Sort';
import { MainSource } from '../../../constants/Source';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import { BI_ERRORS, errorLogger } from '../../../services/loggers/errorLogger';
import { parentConnection } from '../../../services/parentConnection';
import { callOrUse } from '../../callOrUse';
import { DataProvider, ItemsLoader } from '../../ItemsLoader';
import { MediaManagerSDKFolder, MediaManagerSDKResponse } from '../../media-manager-sdk';
import { getAdapter, getAdapterWithAdapterSettings } from '../../media-manager-sdk/getAdapter';
import { isBoards, isSiteFiles, isWixSite } from '../../media-manager-sdk/sources';
import { withSearchAndFiltersAnalytics } from '../bi/withSearchAndFiltersAnalytics';
import { memoizeHandlers } from '../memoizeHandlers';
import { withLoadingErrorCatcher } from '../withLoadingErrorCatcher';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import { withoutRouter } from '../withoutRouter';
import { withSources } from '../withSources';
import { fakeDataResponse, wixVideoLibraryLink } from './wixVideoLibraryLink';
import {
  IWithItemsLoaderInner,
  IWithItemsLoaderOuter,
  saveScrollElementNodeContextTypes,
  TGetItemsLoaderConfig,
} from './withItemsLoader.types';
import { isSameSiteToken } from '../../hooks/useIsCurrentSite';

const getSearchQuery = pathOr<string>('', ['loaderOptions', 'searchQuery']);
const getSearchCacheKiller = pathOr<string>('', ['loaderOptions', 'searchCacheKiller']);
const getRecommendedCategories = pathOr<string>('', ['loaderOptions', 'recommendedCategories']);

export const withItemsLoader = (getItemsLoaderConfig: TGetItemsLoaderConfig = {}, loadConfigDeps?: string[]) => {
  let currentItemsLoaderId = null;

  return compose<IWithItemsLoaderInner, IWithItemsLoaderOuter>(
    setPropTypes({
      source: string.isRequired,
      isPathFolderValid: bool.isRequired,
    }),
    setDisplayName('withItemsLoader'),
    withSources,
    withLoadingErrorCatcher,
    withPropsOnChange(loadConfigDeps || T, (props) => ({
      loaderConfig: callOrUse(getItemsLoaderConfig, props),
    })),
    withPropsOnChange(['currentFolder'], ({ currentFolder }) => ({
      isCurrentFolderLoaded: Boolean(currentFolder),
    })),
    withState('foldersDataProvider', 'setFoldersDataProvider', null),
    withState('filesDataProvider', 'setFilesDataProvider', null),
    withState('foldersSearchDataProvider', 'setFoldersSearchDataProvider', null),
    withState('filesSearchDataProvider', 'setFilesSearchDataProvider', null),
    withHandlers({
      recreateDataProviders:
        ({
          mediaManagerApi,
          searchFoldersMP,
          setFoldersDataProvider,
          setFilesDataProvider,
          setFoldersSearchDataProvider,
          setFilesSearchDataProvider,
        }) =>
        () =>
          Promise.all([
            new Promise((resolve) =>
              setFoldersDataProvider(new DataProvider((...args) => mediaManagerApi.listFolders(...args)), resolve),
            ),
            new Promise((resolve) =>
              setFilesDataProvider(new DataProvider((...args) => mediaManagerApi.listItems(...args)), resolve),
            ),
            new Promise((resolve) => setFoldersSearchDataProvider(new DataProvider(searchFoldersMP), resolve)),
            new Promise((resolve) =>
              setFilesSearchDataProvider(new DataProvider((...args) => mediaManagerApi.searchItems(...args)), resolve),
            ),
          ]),
    }),
    withHandlers({
      cancelDataProviders:
        ({ foldersDataProvider, filesDataProvider, foldersSearchDataProvider, filesSearchDataProvider }) =>
        () => {
          if (foldersDataProvider) {
            foldersDataProvider.cancel();
          }
          if (filesDataProvider) {
            filesDataProvider.cancel();
          }
          if (foldersSearchDataProvider) {
            foldersSearchDataProvider.cancel();
          }
          if (filesSearchDataProvider) {
            filesSearchDataProvider.cancel();
          }
        },
    }),
    withHandlers({
      recreateFilesDataProviders:
        ({ mediaManagerApi, setFilesDataProvider, setFilesSearchDataProvider }) =>
        () =>
          Promise.all([
            new Promise((resolve) =>
              setFilesDataProvider(new DataProvider((...args) => mediaManagerApi.listItems(...args)), resolve),
            ),
            new Promise((resolve) =>
              setFilesSearchDataProvider(new DataProvider((...args) => mediaManagerApi.searchItems(...args)), resolve),
            ),
          ]),
    }),
    withMMGRSettings,
    withProps(
      ({
        isPathFolderValid,
        publicMediaFolderId,
        filesDataProvider,
        foldersDataProvider,
        foldersSearchDataProvider,
        filesSearchDataProvider,
        loaderConfig,
        isSearch,
        isSiteFiles,
        isAllMediaSource,
        isPublicMediaLike,
        isTrash,
        isExplorableMedia,
        isMediaFromWixFoldersLoading,
        areShutterstockCategoriesLoading,
        isWixSitesSource,
        isBoardsRoot,
        mmgrSettings,
        folderId,
      }) => {
        const { hasWixVideoUploads } = mmgrSettings;

        let itemsLoader;

        const wixVideoLibraryProvider = new DataProvider(
          (folderId, _options): MediaManagerSDKResponse<MediaManagerSDKFolder[]> =>
            Promise.resolve(
              fakeDataResponse(
                isSiteFiles && hasWixVideoUploads && folderId === SystemFolderId.SiteFiles ? wixVideoLibraryLink : [],
              ),
            ),
        );

        const isWixSitesRoot = isWixSitesSource && folderId === SystemFolderId.WixSites;

        if (isSearch && !loaderConfig.ignoreSearch) {
          if (isWixSitesRoot) {
            // At WixSites root, search for sites using folders search
            itemsLoader = new ItemsLoader([foldersSearchDataProvider]);
          } else if (isWixSitesSource) {
            // Inside a site, search for both folders and files
            itemsLoader = new ItemsLoader([foldersSearchDataProvider, filesSearchDataProvider]);
          } else {
            // MultiSearch does file and folder search in one request
            itemsLoader = new ItemsLoader([filesSearchDataProvider]);
          }
        } else if (isExplorableMedia || isBoardsRoot) {
          itemsLoader = new ItemsLoader([filesDataProvider]);
        } else if (loaderConfig.foldersOnly) {
          itemsLoader = new ItemsLoader([foldersDataProvider]);
        } else {
          itemsLoader = new ItemsLoader([wixVideoLibraryProvider, foldersDataProvider, filesDataProvider]);
        }

        return {
          canItemsBeLoaded: Boolean(
            ((!isPublicMediaLike && isPathFolderValid) || (publicMediaFolderId && !isMediaFromWixFoldersLoading)) &&
              !areShutterstockCategoriesLoading,
          ),
          itemsLoader,
        };
      },
    ),
    withHandlers(() => {
      const resetFilesStream = new Subject();
      let scrollElementNodeForItemsLoader = null;

      return {
        saveScrollElementNodeForItemsLoader: () => (node) => (scrollElementNodeForItemsLoader = node),
        getScrollElementNodeForItemsLoader: () => () => scrollElementNodeForItemsLoader,
        itemsComparison:
          ({
            source,
            sort,
            loaderConfig,
            mediaType,
            isSearch,
            canItemsBeLoaded,
            folderId,
            siteMediaToken,
            siteSorting,
          }) =>
          () =>
            join(',', [
              getRecommendedCategories(loaderConfig),
              source,
              sort,
              folderId,
              getSearchQuery(loaderConfig),
              getSearchCacheKiller(loaderConfig),
              isSearch ? mediaType : '',
              canItemsBeLoaded,
              siteMediaToken,
              siteSorting,
            ]),
        listItems:
          ({
            folderId,
            publicMediaFolderId,
            sort,
            mediaType,
            mediaTypes,
            itemsLoader,
            loaderConfig,
            isPublicMediaLike,
            canItemsBeLoaded,
            publicMediaFilters,
            isSearch,
            setLoadingError,
            publicMediaMediaType,
            resetLoadingError,
            isGooglePhotosSource,
            isUnsplash,
            source,
            mmgrSettings,
          }) =>
          () => {
            const requestFolderId = isPublicMediaLike ? publicMediaFolderId : folderId;
            const { isPublicMediaScoringProfileEnabled, team, country, hasWixVideoUploads, onlyCurrentUser } =
              mmgrSettings;

            const requestOptions = {
              mediaType: isPublicMediaLike ? publicMediaMediaType : mediaType,
              sort: SORTING_OPTIONS[sort],
              paging: {
                size: isGooglePhotosSource
                  ? GOOGLE_PHOTOS_PAGE_SIZE
                  : isUnsplash
                  ? UNSPLASH_PAGE_SIZE
                  : DEFAULT_PAGE_SIZE,
              },
              ...loaderConfig.loaderOptions,
              ...(isSearch ? { searchQuery: getSearchQuery(loaderConfig) } : {}),
              ...((isSiteFiles(source) || isWixSite(source)) && onlyCurrentUser ? { onlyCurrentUser: true } : {}),
              ...(isSearch ? { mediaTypes } : {}),
              ...(isBoards(source) ? { isTeam: !isNil(team) } : {}),
              ...(isPublicMediaLike
                ? {
                    country,
                    filters: publicMediaFilters,
                    isPublicMediaScoringProfileEnabled,
                    callSource: MainSource.MediaFromWix,
                  }
                : {}),
              ...(isWixSite(source) ? { includeCurrentSite: hasWixVideoUploads } : {}),
            };

            currentItemsLoaderId = canItemsBeLoaded ? itemsLoader.id : null;

            const privateSiteAdapter = getAdapter(MainSource.SiteFiles, mmgrSettings);
            const currentAdapter = getAdapter(source, mmgrSettings);

            if (currentAdapter.correlationId) {
              const correlationIdFromCurrentAdapter = currentAdapter.correlationId.get();

              const correlationIdFromPrivateAdapter = privateSiteAdapter.correlationId.get();

              // when Media Manager is first open, we need to take same correlation id as in getSitesInfo and getManagerInfo,
              // which are in privateSite adapter. After that we get correlationId from current adapter.
              // For FFW/Shutterstock correlationId is set in withPublicMedia/withShutterstock
              const correlationId = correlationIdFromCurrentAdapter || correlationIdFromPrivateAdapter;

              if (correlationId) {
                currentAdapter.correlationId.set(correlationId);
              } else {
                currentAdapter.correlationId.randomize();
              }
            }

            return canItemsBeLoaded
              ? itemsLoader
                  .loadItems(requestFolderId, requestOptions)
                  .then((items) => {
                    // we need this in case, when user clicks on tab quickly and everything get frozen
                    resetLoadingError();

                    privateSiteAdapter.correlationId.reset();
                    if (currentAdapter.correlationId) {
                      currentAdapter.correlationId.reset();
                    }

                    if (isWixSite(source)) {
                      const currentSite = find(isSameSiteToken(mmgrSettings.siteMediaToken), items);
                      if (!currentSite) {
                        // current site has no Video Library and `includeCurrentSite` is false
                        return items;
                      }

                      return [
                        currentSite,
                        ...filter(
                          (item: MediaManagerSDKFolder) => not(isSameSiteToken(mmgrSettings.siteMediaToken)(item)),
                          items,
                        ),
                      ];
                    }

                    return items;
                  })
                  .catch((reason) => {
                    if (currentItemsLoaderId === itemsLoader.id) {
                      setLoadingError(reason);

                      return Promise.reject(reason);
                    }
                  })
                  .catch(errorLogger.createLoggingHandler(BI_ERRORS.mediaManagerSDK.listItems))
              : null;
          },
        getLoaderHasMoreItems:
          ({ itemsLoader, canItemsBeLoaded }) =>
          () =>
            canItemsBeLoaded && itemsLoader.hasMoreItems,
        resetFilesLoader:
          ({ filesDataProvider, filesSearchDataProvider, recreateFilesDataProviders }) =>
          async () => {
            filesDataProvider.reset();
            filesSearchDataProvider.reset();
            await recreateFilesDataProviders();
            resetFilesStream.next(null);
          },
        resetLoader:
          ({ recreateDataProviders }) =>
          async () =>
            recreateDataProviders(),
        getResetFilesStream: () => () => resetFilesStream,
      };
    }),
    withContext(saveScrollElementNodeContextTypes, pick(['saveScrollElementNodeForItemsLoader'])),
    withSearchAndFiltersAnalytics,
    withRouter,
    withHandlers({
      createItemsRequestRefetchConfig:
        ({
          resetLoader,
          listItems,
          isSearch,
          itemsComparison,
          location,
          setRecentSearches,
          getRecentSearches,
          trackSearchAndFiltersSubscribeToLoadFinish,
          canItemsBeLoaded,
          source,
          searchQuery,
          mmgrSettings,
        }) =>
        (additionalFields = {}) => ({
          value: async () => {
            if (!canItemsBeLoaded) {
              return null;
            }

            await resetLoader();
            const listItemsPromise = listItems();
            if (listItemsPromise) {
              trackSearchAndFiltersSubscribeToLoadFinish(listItemsPromise, location.pathname);

              const { siteMediaToken } = mmgrSettings;

              const notMultiSearch = contains(source, [
                MainSource.Shutterstock,
                MainSource.ShutterstockVideo,
                MainSource.Unsplash,
              ]);

              if (isSearch) {
                if (getRecentSearches) {
                  listItemsPromise.then(() => {
                    const recentSearches = getRecentSearches();

                    if (recentSearches) {
                      setRecentSearches(recentSearches);
                    }
                  });
                } else if (notMultiSearch) {
                  const multiSearchAdapter = getAdapterWithAdapterSettings(MainSource.MultiSearch, mmgrSettings);
                  const { recentSearches } = await multiSearchAdapter.search.search(searchQuery, {
                    siteMediaToken,
                  });
                  if (recentSearches) {
                    setRecentSearches(recentSearches);
                  }
                }
              }
            }
            return listItemsPromise;
          },
          comparison: itemsComparison(),
          ...additionalFields,
        }),
    }),
    withoutMMGRSettings,
    withoutRouter,
    connect<any>(({ listItems, createItemsRequestRefetchConfig }) => ({
      itemsRequest: createItemsRequestRefetchConfig(),
      loadMoreItems: () => ({
        nextPageItemsRequest: {
          value: listItems(),
          force: true,
        },
      }),
      resetLoadMoreItems: () => ({
        nextPageItemsRequest: {
          value: [],
          force: true,
        },
      }),
      refreshFolder: () => ({
        itemsRequest: createItemsRequestRefetchConfig({ force: true }),
      }),
    })),
    memoizeHandlers('loadMoreItems', 'resetLoadMoreItems', 'refreshFolder'),
    withStateHandlers({ hasMoreItems: false }, { setHasMoreItems: () => (hasMoreItems) => ({ hasMoreItems }) }),
    withLifecycle({
      onDidMount({ refreshFolder }) {
        parentConnection.on('settings', refreshFolder);
      },
      onWillUnmount({ refreshFolder, cancelDataProviders }) {
        cancelDataProviders();
        parentConnection.off('settings', refreshFolder);
      },
      async onDidUpdate(
        { mediaType: prevMediaType },
        {
          getLoaderHasMoreItems,
          setHasMoreItems,
          hasMoreItems,
          mediaType,
          resetFilesLoader,
          resetLoadMoreItems,
          getScrollElementNodeForItemsLoader,
        },
      ) {
        if (prevMediaType !== mediaType) {
          const scrollElementNode = getScrollElementNodeForItemsLoader();
          if (scrollElementNode && scrollElementNode.el) {
            scrollElementNode.el.scrollTop = 0;
          }

          await resetFilesLoader();
          resetLoadMoreItems();
        }

        if (getLoaderHasMoreItems() !== hasMoreItems) {
          setHasMoreItems(getLoaderHasMoreItems());
        }
      },
    }),
    withProps(
      ({
        itemsRequest,
        nextPageItemsRequest,
        getResetFilesStream,
        hasMoreItems,
        isCurrentFolderLoaded,
        isMediaFromWixFoldersLoading,
        areShutterstockCategoriesLoading,
      }) => {
        const isLoading = Boolean(
          !isCurrentFolderLoaded ||
            itemsRequest.pending ||
            isMediaFromWixFoldersLoading ||
            areShutterstockCategoriesLoading,
        );
        const areNextFilesLoaded = Boolean(nextPageItemsRequest && !nextPageItemsRequest.pending);
        const areNextFilesLoading = Boolean(nextPageItemsRequest && nextPageItemsRequest.pending);

        return {
          resetFilesStream: getResetFilesStream(),
          hasMoreItems: hasMoreItems && !areNextFilesLoading,
          isLoading,
          items: itemsRequest.value || [],
          nextPageItems: areNextFilesLoaded ? nextPageItemsRequest.value : [],
          areNextFilesLoading,
        };
      },
    ),
    omitProps(
      'loaderConfig',
      'isCurrentFolderLoaded',
      'itemsLoader',
      'itemsRequest',
      'resetLoadMoreItems',
      'filesDataProvider',
      'foldersDataProvider',
      'foldersSearchDataProvider',
      'filesSearchDataProvider',
      'nextPageItemsRequest',
    ),
  );
};

export const withSaveScrollElementNodeContext = compose(
  getContext(saveScrollElementNodeContextTypes),
  defaultProps({
    saveScrollElementNodeForItemsLoader: noop,
  }),
);
